// R36 family product pages (R36H Pro Max, R36S Plus, R36XX). Copy of the R36 Max page script, kept separate so the
// R36 Max, R36H and Ultra pages never change. Options are read from the SKU (<MODEL>-<COLOUR>-<CAPACITY>);
// colour codes and swatches come from the section JSON, so one file serves the three models.
class R36FamilyProductExperience extends HTMLElement {
  connectedCallback() {
    const dataNode = this.querySelector('[data-r36-json]');
    if (!dataNode) return;
    this.data = JSON.parse(dataNode.textContent);
    if (!this.data.variants?.length) return;

    const colourCodes = this.data.colourCodes || {};
    this.optionNames = ['color', 'storage'];
    this.data.variants.forEach((variant) => {
      const parts = String(variant.sku || '').split('-');
      variant.options = [colourCodes[parts[1]] || parts[1] || '', (parts[2] || '').includes('128') ? '128' : '64'];
    });

    this.form = this.querySelector('.r36xp__form');
    this.idInput = this.querySelector('[data-r36-variant-id]');
    this.optionsRoot = this.querySelector('[data-r36-options]');
    this.priceTargets = this.querySelectorAll('[data-r36-price]');
    this.summaryTargets = this.querySelectorAll('[data-r36-summary]');
    this.buttons = this.querySelectorAll('[data-r36-submit]');

    const queryId = new URL(window.location.href).searchParams.get('variant');
    const initialVariant = this.data.variants.find((variant) => String(variant.id) === queryId)
      || this.data.variants.find((variant) => String(variant.id) === String(this.data.initialVariantId))
      || this.data.variants.find((variant) => variant.available)
      || this.data.variants[0];

    this.selected = [...initialVariant.options];
    this.renderOptions();
    this.form?.addEventListener('submit', (event) => { event.preventDefault(); this.addToCart(); });
    this.updateVariant({ updateUrl: false });
    this.bindGallery();
    this.bindStickyBar();
  }

  renderOptions() {
    if (!this.optionsRoot) return;
    this.optionNames.forEach((name, optionIndex) => {
      const isColor = name === 'color';
      const values = [...new Set(this.data.variants.map((variant) => variant.options[optionIndex]))];
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'r36xp__option ' + (isColor ? 'r36xp__option--color' : 'r36xp__option--storage');
      const legend = document.createElement('legend');
      legend.textContent = isColor ? this.data.ui.ui_color : this.data.ui.ui_storage;
      legend.dataset.step = String(optionIndex + 1).padStart(2, '0');
      fieldset.append(legend);
      const list = document.createElement('div');
      list.className = isColor ? 'r36xp__choices r36xp__choices--colors' : 'r36xp__choices';

      values.forEach((value, valueIndex) => {
        const id = `${this.id}-${optionIndex}-${valueIndex}`;
        const wrap = document.createElement('div');
        wrap.className = 'r36xp__choice';
        const input = document.createElement('input');
        input.type = 'radio';
        input.id = id;
        input.name = `${this.id}-${name}`;
        input.value = value;
        input.setAttribute('aria-label', this.displayValue(optionIndex, value));
        input.dataset.optionIndex = String(optionIndex);
        input.addEventListener('change', () => {
          this.selected[optionIndex] = value;
          this.reconcileSelection(optionIndex);
          this.updateVariant({ resetGallery: isColor });
        });
        const label = document.createElement('label');
        label.htmlFor = id;
        const text = document.createElement('span');
        text.className = 'r36xp__choice-copy';
        if (isColor) {
          const dot = document.createElement('span');
          dot.className = 'r36xp__dot';
          dot.style.setProperty('--dot', this.colorValue(value));
          label.append(dot);
          text.textContent = this.displayValue(optionIndex, value);
        } else {
          const title = document.createElement('strong');
          title.textContent = this.displayValue(optionIndex, value);
          const detail = document.createElement('small');
          detail.textContent = value === '128' ? this.data.ui.ui_space : this.data.ui.ui_library;
          const extra = document.createElement('span');
          extra.className = 'r36xp__choice-extra';
          extra.dataset.storageExtra = value;
          text.append(title, detail, extra);
        }
        label.append(text);
        wrap.append(input, label);
        list.append(wrap);
      });
      fieldset.append(list);
      this.optionsRoot.append(fieldset);
    });
  }

  displayValue(optionIndex, value) {
    if (this.optionNames[optionIndex] === 'color') return this.data.ui[value] || value;
    return `${value} ${this.data.ui.unit}`;
  }

  colorValue(value) {
    return (this.data.swatches || {})[value] || '#777';
  }

  currentVariant() {
    return this.data.variants.find((variant) => variant.options.every((option, index) => option === this.selected[index]));
  }

  reconcileSelection(changedIndex) {
    if (this.currentVariant()) return;
    const candidates = this.data.variants.filter((variant) => variant.options[changedIndex] === this.selected[changedIndex]);
    if (!candidates.length) return;
    const score = (variant) => variant.options.reduce((total, option, index) => {
      if (index === changedIndex) return total;
      return total + (option === this.selected[index] ? 2 : 0) + (variant.available ? 1 : 0);
    }, 0);
    this.selected = [...[...candidates].sort((a, b) => score(b) - score(a))[0].options];
  }

  syncOptionInputs() {
    this.querySelectorAll('.r36xp__choice input').forEach((input) => {
      const optionIndex = Number(input.dataset.optionIndex);
      input.checked = input.value === this.selected[optionIndex];
      input.disabled = !this.data.variants.some((variant) => variant.options[optionIndex] === input.value
        && variant.options.every((option, index) => index === optionIndex || option === this.selected[index]));
    });
  }

  updateVariant({ updateUrl = true, resetGallery = false } = {}) {
    const variant = this.currentVariant();
    this.syncOptionInputs();
    const summary = this.selected.map((value, index) => this.displayValue(index, value)).join(' · ');
    this.summaryTargets.forEach((node) => { node.textContent = summary; });

    if (!variant) {
      if (this.idInput) { this.idInput.value = ''; this.idInput.disabled = true; }
      this.priceTargets.forEach((node) => { node.textContent = this.data.ui.soldout; });
      this.setSubmitState(false, this.data.ui.ui_unavailable);
      return;
    }
    if (this.idInput) { this.idInput.value = variant.id; this.idInput.disabled = false; }
    this.priceTargets.forEach((node) => { node.textContent = this.formatPrice(variant); });

    const base = this.data.variants.find((candidate) => candidate.options[0] === this.selected[0] && candidate.options[1] === '64');
    this.querySelectorAll('[data-storage-extra]').forEach((node) => {
      const match = this.data.variants.find((candidate) => candidate.options[0] === this.selected[0] && candidate.options[1] === node.dataset.storageExtra);
      const difference = match && base ? match.price - base.price : 0;
      node.textContent = difference > 0 ? '+ ' + this.formatPrice({ price: difference }) : this.data.ui.ui_included;
    });
    this.setSubmitState(variant.available, variant.available ? this.data.ui.add : this.data.ui.soldout);
    this.updatePresentation(variant, { resetGallery });

    if (updateUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  formatPrice(variant) {
    if (!Number.isFinite(variant.price)) return variant.priceFormatted || '';
    return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
      style: 'currency', currency: this.data.currency || 'EUR'
    }).format(variant.price / 100);
  }

  setSubmitState(enabled, labelText) {
    this.buttons.forEach((button) => {
      button.disabled = !enabled || this.busy;
      const label = button.querySelector('[data-r36-submit-label]');
      if (label) label.textContent = this.busy ? this.data.ui.adding : labelText;
    });
  }

  async addToCart() {
    const variant = this.currentVariant();
    if (this.busy || !variant?.available) return;
    const error = this.querySelector('.product-form__error-message-wrapper');
    if (error) error.hidden = true;
    this.busy = true;
    this.form.setAttribute('aria-busy', 'true');
    const controls = [...this.form.querySelectorAll('input[type="radio"]')];
    const previous = controls.map((input) => input.disabled);
    controls.forEach((input) => { input.disabled = true; });
    this.setSubmitState(false, this.data.ui.adding);
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const root = window.Shopify?.routes?.root || '/';
    const payload = { items: [{ id: variant.id, quantity: 1 }] };
    if (cart?.getSectionsToRender) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      cart.setActiveElement?.(document.activeElement);
    }
    let added = false;
    try {
      // Request objects avoid third-party string-URL interceptors that may retry POSTs.
      const response = await fetch(new Request(window.location.origin + root + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }));
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.data.ui.error);
      added = true;
      const homeCart = document.querySelector('moonbundle-cart-drawer-block');
      if (typeof homeCart?.open === 'function') homeCart.open(true);
      else if (cart?.renderContents && result.sections) {
        cart.classList.remove('is-empty');
        cart.renderContents(result);
      } else window.location.assign(root + 'cart');
    } catch (failure) {
      // A drawer-render failure must never invite a duplicate add.
      if (added) window.location.assign(root + 'cart');
      else if (error) {
        error.hidden = false;
        error.querySelector('.product-form__error-message').textContent = failure instanceof TypeError ? this.data.ui.error : failure.message || this.data.ui.error;
      }
    } finally {
      this.busy = false;
      this.form.removeAttribute('aria-busy');
      controls.forEach((input, index) => { input.disabled = previous[index]; });
      this.setSubmitState(variant.available, this.data.ui.add);
    }
  }

  updatePresentation(variant, { resetGallery = false } = {}) {
    const heroThumb = this.querySelector('[data-r36-thumb][data-view="hero"]');
    if (heroThumb && variant.featuredImage) {
      const alt = [this.data.productLabel, this.displayValue(0, variant.options[0])].join(' · ');
      heroThumb.dataset.src = variant.featuredImage;
      heroThumb.dataset.alt = alt;
      heroThumb.dataset.caption = alt;
      const heroImage = heroThumb.querySelector('img');
      if (heroImage) heroImage.src = variant.featuredImage;
    }
    const activeThumb = resetGallery && heroThumb
      ? heroThumb
      : this.querySelector('[data-r36-thumb][aria-current="true"]') || heroThumb;
    if (activeThumb) this.showGalleryImage(activeThumb);
  }

  showGalleryImage(thumb) {
    const main = this.querySelector('[data-r36-main-image]');
    const caption = this.querySelector('[data-r36-caption]');
    if (!main || !caption) return;
    main.src = thumb.dataset.src;
    main.alt = thumb.dataset.alt;
    caption.textContent = thumb.dataset.caption;
    this.querySelectorAll('[data-r36-thumb]').forEach((item) => item.setAttribute('aria-current', String(item === thumb)));
  }

  bindGallery() {
    this.querySelectorAll('[data-r36-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.showGalleryImage(thumb));
    });
  }

  bindStickyBar() {
    // 07/10/2026 : barre d'achat mobile (<= 749 px). Visible dès que le bouton d'achat natif est hors écran
    // (avant comme après lui), vignette = image déjà chargée par la galerie, posée au-dessus de la bannière cookies.
    const bar = this.querySelector('[data-r36-sticky]');
    const native = this.querySelector('[data-r36-purchase] [data-r36-submit]');
    if (!bar || !native || !('IntersectionObserver' in window)) return;
    const mobile = window.matchMedia('(max-width: 749px)');
    const heroImage = this.querySelector('[data-r36-thumb][data-view="hero"] img');
    if (heroImage && !bar.querySelector('.r36xp__sticky-thumb')) {
      const thumb = document.createElement('img');
      thumb.className = 'r36xp__sticky-thumb';
      thumb.alt = '';
      thumb.width = 44;
      thumb.height = 44;
      thumb.decoding = 'async';
      thumb.src = heroImage.src;
      bar.prepend(thumb);
      new MutationObserver(() => { thumb.src = heroImage.src; }).observe(heroImage, { attributes: true, attributeFilter: ['src'] });
    }
    let nativeInView = true;
    const sync = () => {
      const show = mobile.matches && !nativeInView;
      bar.classList.toggle('is-visible', show);
      bar.toggleAttribute('inert', !show);
      bar.setAttribute('aria-hidden', String(!show));
    };
    new IntersectionObserver(([entry]) => { nativeInView = entry.isIntersecting; sync(); }).observe(native);
    if (mobile.addEventListener) mobile.addEventListener('change', sync);
    const consent = document.getElementById('r36s-cc');
    if (!consent) return;
    const lift = () => {
      const shown = getComputedStyle(consent).display !== 'none';
      const height = shown ? Math.max(0, Math.round(window.innerHeight - consent.getBoundingClientRect().top)) : 0;
      bar.style.setProperty('--r36-sticky-offset', height + 'px');
      bar.toggleAttribute('data-cc-lifted', height > 0);
    };
    lift();
    if ('ResizeObserver' in window) new ResizeObserver(lift).observe(consent);
    new MutationObserver(lift).observe(consent, { attributes: true });
    new MutationObserver(lift).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', lift, { passive: true });
  }
}

if (!customElements.get('r36fam-product-experience')) {
  customElements.define('r36fam-product-experience', R36FamilyProductExperience);
}
